import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core";
import { Cell, PieChart, Pie, Sector, ResponsiveContainer, Label, Tooltip } from "recharts";

import { COLORS } from "../../theme/colors";
import { CustomPieChartTooltipContent } from "./CustomPieChartTooltipContent";
import { nKilowattFormatter, currencyFormatter, nFormatter, getPercent } from "../../services";

const useStyles = makeStyles(() => ({
  centerTopLabel: {
    fontSize: 11,
    transform: "translateY(-28px)",
  },
  centerLabel: {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 24,
  },
  centerBottomLabel: {
    fontSize: 11,
    transform: "translateY(28px)",
  },
}));

// Example https://recharts.org/en-US/examples/CustomActiveShapePieChart
export const CustomPieChart = ({ data, name, showTooltip }) => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const [activeIndex, setActiveIndex] = useState(null);

  const total = useMemo(() => {
    let t = data.reduce((prev, current) => prev + current.value, 0);
    if (!Number.isInteger(t)) {
      t = t.toFixed(1);
    }
    return t;
  }, [data]);

  // Label in center of the piechart issue
  // https://github.com/recharts/recharts/issues/191
  const centeredLabel = useMemo(() => {
    let displayedValue = total;
    if (activeIndex !== null) {
      displayedValue = data[activeIndex].value;
    }

    switch (name) {
      case "PowerConsumption": {
        let topValue = "Total";
        let centerValue = currencyFormatter(total, currencySymbol);

        if (activeIndex !== null) {
          topValue = data[activeIndex].name;
          centerValue = currencyFormatter(data[activeIndex].value, currencySymbol);
        }

        return (
          <>
            <Label value={topValue} className={classes.centerTopLabel} position="centerTop" />
            <Label value={centerValue} className={classes.centerLabel} position="center" />
          </>
        );
      }
      case "PowerConsumptionKwh":
      case "PowerConsumptionKwp": {
        const suffix = name[name?.length - 1];
        let topValue = "Total";
        let [centerValue, bottomValue] = nKilowattFormatter(total, suffix).split(" ");

        if (activeIndex !== null) {
          topValue = data[activeIndex].name;
          [centerValue, bottomValue] = nKilowattFormatter(data[activeIndex].value, suffix).split(
            " "
          );
        }

        return (
          <>
            <Label value={topValue} className={classes.centerTopLabel} position="centerTop" />
            <Label value={centerValue} className={classes.centerLabel} position="center" />
            <Label
              value={bottomValue}
              className={classes.centerBottomLabel}
              position="centerBottom"
            />
          </>
        );
      }
      case "SolarPowerConsumptionPerYearKwh":
      case "SolarPowerConsumptionKwp": {
        const suffix = name[name?.length - 1];
        let topValue = "Total";
        let [centerValue, bottomValue] = nKilowattFormatter(total, suffix).split(" ");

        return (
          <>
            <Label value={topValue} className={classes.centerTopLabel} position="centerTop" />
            <Label value={centerValue} className={classes.centerLabel} position="center" />
            <Label
              value={bottomValue}
              className={classes.centerBottomLabel}
              position="centerBottom"
            />
          </>
        );
      }
      case "area": {
        let topValue = "Total";
        let centerValue = nFormatter(total);
        let bottomValue = user.unit_system;

        if (activeIndex !== null) {
          topValue = data[activeIndex].name;
          centerValue = nFormatter(data[activeIndex].value);
        }

        return (
          <>
            <Label value={topValue} className={classes.centerTopLabel} position="centerTop" />
            <Label value={centerValue} className={classes.centerLabel} position="center" />
            <Label
              value={bottomValue}
              className={classes.centerBottomLabel}
              position="centerBottom"
            />
          </>
        );
      }
      case "ConnectionStatus": {
        let centerLabel = data[0].centerLabel;
        let centerSuffixLabel = data[0].centerSuffixLabel;
        let topLabel = `${((data[0].value / total) * 100).toFixed(0)} %`;

        if (activeIndex !== null) {
          centerLabel = data[activeIndex].centerLabel;
          centerSuffixLabel = data[activeIndex].centerSuffixLabel;
          topLabel = `${((data[activeIndex].value / total) * 100).toFixed(0)} %`;
        }

        return (
          <>
            <Label
              value={topLabel}
              className={classes.centerTopLabel}
              style={{
                fontSize: 24,
                fontWeight: 700,
              }}
              position="centerTop"
            />
            <Label value={centerLabel} position="center" />
            <Label
              value={centerSuffixLabel}
              position="centerBottom"
              style={{
                transform: "translateY(18px)",
              }}
            />
          </>
        );
      }
      case "SupplyRevenuesTotal": {
        const result = data.find((data) => data.key === "tenants_rebate");
        let recalculateTotal = total;
        let subtitle = "";
        if (result.value !== 0) {
          recalculateTotal = total - result.value;
          // eslint-disable-next-line no-unused-vars
          subtitle = "(Rebate applied)";
        }
        let topValue = "Total";
        let centerValue = currencyFormatter(recalculateTotal, currencySymbol);
        if (activeIndex !== null) {
          topValue = data[activeIndex].name;
          centerValue = currencyFormatter(
            data[activeIndex].key === "tenants_rebate"
              ? -data[activeIndex].value
              : data[activeIndex].value,
            currencySymbol
          );
        }
        return (
          <>
            <Label value={topValue} className={classes.centerTopLabel} position="centerTop" />
            <Label value={centerValue} className={classes.centerLabel} position="center" />
            <Label value={subtitle} className={classes.centerBottomLabel} position="centerBottom" />
          </>
        );
      }
      default:
        return (
          <Label
            className={classes.centeredLable}
            value={currencyFormatter(displayedValue, currencySymbol)}
            position="center"
          />
        );
    }
  }, [
    name,
    total,
    classes.centeredLable,
    activeIndex,
    data,
    classes.centerBottomLabel,
    classes.centerLabel,
  ]);

  const renderCustomizedLabel = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      outerRadius,
      fill,
      value,
      name,
      unit,
      suffix,
      value_position,
      value_percent,
      nameFormat,
      valueFormat,
      onlySuffix,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    // IMPORTANT: If you want to use this code just change labelLine={true}
    // We're using labelLine={false} to hide the line when the
    // value percent is less then 5 from total
    // const sx = cx + (outerRadius + 10) * cos;
    // const sy = cy + (outerRadius + 10) * sin;
    const additionalRadius = Math.abs(sin) > 0.9 ? 40 : 20;
    const sx = cx + outerRadius * cos;
    const sy = cy + outerRadius * sin;
    const mx = cx + (outerRadius + additionalRadius) * cos;
    const my = cy + (outerRadius + additionalRadius) * sin;
    const ex = mx + (cos >= 0 ? 4 : -4);
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    const getValue = () => {
      switch (unit) {
        case "kwh":
          return nKilowattFormatter(value);
        case "kwp":
          return nKilowattFormatter(value, "p");
        case "%":
          return nFormatter(value);
        case "unit_system":
          return `${nFormatter(value)} ${user.unit_system}`;
        default:
          return currencyFormatter(value, currencySymbol);
      }
    };

    const getFormattedValue = () => {
      if (valueFormat) {
        return valueFormat(value, value_percent, unit);
      }

      return getValue();
    };

    const getName = () => {
      if (nameFormat) {
        return nameFormat(name, value, value_percent, unit);
      }

      const displayedValue = value_percent ? value_percent : value;
      return value_position === "inline" ? `${name} - ${displayedValue}${unit}` : name;
    };

    const getLineWidth = (ex, cos) => {
      const nameLength = getName().length;
      const suffixLength = suffix?.length;
      const mainLength =
        onlySuffix && suffixLength && suffixLength > nameLength ? suffixLength : nameLength;
      return ex + (cos >= 0 ? mainLength : -mainLength) * 6;
    };

    if (!value || getPercent(value, total) < 5) return null;

    return (
      <g>
        <path
          d={`M${sx},${sy}L${mx},${my}L${getLineWidth(ex, cos)},${ey}`}
          stroke={fill}
          fill="none"
        />
        <text
          x={ex + (cos >= 0 ? 1 : -1)}
          y={ey}
          textAnchor={textAnchor}
          fill={COLORS.darks[0][100]}
          style={{ transform: "translateY(-5px)", fontSize: 12 }}
        >
          {getName()}
        </text>
        {onlySuffix ? (
          <text
            x={ex + (cos >= 0 ? 1 : -1)}
            y={ey}
            dy={15}
            textAnchor={textAnchor}
            fill={COLORS.darks[0][100]}
            style={{ fontSize: 12 }}
          >
            {suffix}
          </text>
        ) : value_position !== "inline" && suffix ? (
          <>
            <text
              x={ex + (cos >= 0 ? 1 : -1)}
              y={ey}
              dy={15}
              textAnchor={textAnchor}
              fill={COLORS.darks[0][100]}
              style={{ fontSize: 12 }}
            >
              {suffix}
            </text>
            <text
              x={ex + (cos >= 0 ? 1 : -1)}
              y={ey}
              dy={30}
              textAnchor={textAnchor}
              fill={COLORS.darks[0][100]}
              style={{ fontSize: 12, fontWeight: 700 }}
            >
              {getValue()}
            </text>
          </>
        ) : value_position !== "inline" ? (
          <text
            x={ex + (cos >= 0 ? 1 : -1)}
            y={ey - 4}
            dy={15}
            textAnchor={textAnchor}
            fill={getName() === "Tenants rebate" ? COLORS.red : COLORS.darks[0][100]}
            style={{ fontSize: 12, fontWeight: 700 }}
          >
            {getName() === "Tenants rebate" ? "-" + getFormattedValue() : getFormattedValue()}
          </text>
        ) : null}
      </g>
    );
  };

  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius - 2}
          outerRadius={outerRadius + 2}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          stroke={payload?.strokeColor ?? payload.color}
          strokeWidth={payload?.strokeWidth ?? 0}
          strokeDasharray="2 2"
        />
      </g>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart width={400} height={400}>
        {showTooltip ? (
          <Tooltip content={<CustomPieChartTooltipContent total={total} chartTypeName={name} />} />
        ) : null}
        <Pie
          activeIndex={activeIndex}
          activeShape={renderActiveShape}
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          startAngle={90}
          endAngle={-270}
          dataKey="value"
          labelLine={false}
          label={renderCustomizedLabel}
          onMouseEnter={(_, index) => setActiveIndex(index)}
          onMouseLeave={() => setActiveIndex(null)}
        >
          {centeredLabel}
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.value ? entry.color : "transparent"}
              stroke={entry?.strokeColor ?? entry.color}
              strokeWidth={entry?.strokeWidth ?? 0}
              strokeDasharray="2 2"
            />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
};
